import { useId, useState } from 'react'
import {
  Box,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { Keyboard } from '@mui/icons-material'
import { controlButtonSx } from './constants'
import { IShortcutHint } from './shortcuts'

interface IShortcutsHelp {
  open: boolean
  onOpenChange: (open: boolean) => void
  shortcuts: IShortcutHint[]
}

/** Botão e lista dos atalhos de teclado. Não aparece em telas `xs`, onde não há teclado físico. */
const ShortcutsHelp: React.FC<IShortcutsHelp> = ({
  open,
  onOpenChange,
  shortcuts
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const titleId = useId()
  const popoverId = useId()

  if (isXs) return null

  return (
    <>
      <IconButton
        ref={setAnchorEl}
        onClick={() => onOpenChange(!open)}
        size='small'
        aria-label='Atalhos de teclado'
        title='Atalhos de teclado (?)'
        aria-keyshortcuts='?'
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        sx={controlButtonSx}
      >
        <Keyboard fontSize='small' />
      </IconButton>
      <Popover
        id={popoverId}
        open={open && anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => onOpenChange(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': titleId,
            sx: { p: 2, maxWidth: 360 }
          }
        }}
      >
        <Typography id={titleId} variant='subtitle2' sx={{ mb: 1 }}>
          Atalhos de teclado
        </Typography>
        <Box
          component='dl'
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            columnGap: 2,
            rowGap: 0.75,
            m: 0
          }}
        >
          {shortcuts.map(({ label, display }) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Typography component='dt' variant='body2'>
                {label}
              </Typography>
              <Box component='dd' sx={{ m: 0, justifySelf: 'end' }}>
                <Box
                  component='kbd'
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {display}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}

export default ShortcutsHelp
